// src/components/operationCenter/widgets/CorrelationMatrixWidget.tsx
// Widget wrapper for CorrelationChart from TokenScreener

import React, { useMemo, useState, useCallback, useEffect } from "react";
import type { WidgetProps, CorrelationMatrixConfig } from "@/types/dashboard";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useMetricsCatalog } from "@/hooks/useMetricsCatalog";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import { useTokenCategoryMap } from "@/hooks/tokens/compare/useCompareData";
import { filterTokenRows } from "@/utils/tokens";
import { TimeRangePicker } from "@/components/common";
import { MetricKeys } from "@/constants/metrics";
import CorrelationChart from "@/components/tokenScreener/charts/CorrelationChart";
import type { MetricInfo } from "@/types/metrics";
import type { CategoryTreeNode } from "@/types/dashboards/categories";
import type { TokenRow } from "@/types/movers";

type RangeKey = '1d' | '7d' | '30d' | '365d';

interface MetricOption {
  key: string;
  label: string;
}

const METRIC_OPTIONS: MetricOption[] = [
  { key: MetricKeys.PriceClose, label: "Price" },
  { key: MetricKeys.Volume, label: "Volume" },
  { key: MetricKeys.TVL, label: "TVL" },
  { key: MetricKeys.MarketCap, label: "Market Cap" },
];

// Set of valid metric keys for quick lookup
const VALID_METRIC_KEYS = new Set(METRIC_OPTIONS.map(m => m.key));

// Validate and normalize metric key
const getValidMetricKey = (configValue: string | undefined, defaultValue: string): string => {
  if (configValue && VALID_METRIC_KEYS.has(configValue)) {
    return configValue;
  }
  return defaultValue;
};

const CorrelationMatrixWidget: React.FC<WidgetProps<CorrelationMatrixConfig>> = ({ 
  widget,
  onConfigChange 
}) => {
  const config = widget.config as CorrelationMatrixConfig;

  // State - initialized from config with validation
  const [selectedMetric, setSelectedMetric] = useState(() => 
    getValidMetricKey(config.metric, MetricKeys.PriceClose)
  );
  const [range, setRange] = useState<RangeKey>((config.time_range as RangeKey) ?? '7d');
  const initialCategory = useMemo(
    () => config.category ?? 'all',
    [config.category]
  );
  const [category, setCategory] = useState<string>(initialCategory);

  // Data fetching
  const { data: rows } = useTokensScreenerData();
  const { roots: categoryRoots, maps } = useCategoriesTree();
  const { data: tokenCategoryMap } = useTokenCategoryMap();

  // Metrics catalog
  const { data: catalog } = useMetricsCatalog();
  const catalogMap: Record<string, MetricInfo> = useMemo(() => {
    const map: Record<string, MetricInfo> = {};
    (catalog || []).forEach((m) => { 
      if ((m as MetricInfo)?.metric_key) map[m.metric_key] = m as MetricInfo; 
    });
    return map;
  }, [catalog]);

  // Label lookup with fallback
  const labelFromCatalog = useCallback((key: string): string => {
    const entry = catalogMap[key] as unknown as { display_name?: string | null; label?: string | null; name?: string | null } | undefined;
    return (entry?.display_name || entry?.label || entry?.name || METRIC_OPTIONS.find(m => m.key === key)?.label || key);
  }, [catalogMap]);

  // Filter rows by category
  const rowsFiltered = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return filterTokenRows(rows as TokenRow[], {
      search: '',
      mcMin: '',
      mcMax: '',
      category,
      maps,
      tokenCategoryMap: (tokenCategoryMap as unknown as Record<string, number[]>) || {},
      excludeStables: true,
      getTicker: (r) => String(r["tokenSymbol"] ?? ''),
      getName: (r) => String(r["tokenName"] ?? r["name"] ?? ''),
      getMarketCap: (r) => (r["market_cap"] as number | null) ?? (r[MetricKeys.MarketCap] as number | null) ?? null,
    });
  }, [rows, category, maps, tokenCategoryMap]);

  // Get flat list of categories
  const categoryOptions = useMemo(() => {
    if (!categoryRoots) return [];
    const getLeaves = (node: CategoryTreeNode): CategoryTreeNode[] => {
      if (node.is_leaf) return [node];
      return (node.children || []).flatMap(getLeaves);
    };
    return categoryRoots
      .filter((c) => c.category_code !== 'overview')
      .flatMap((root) => getLeaves(root));
  }, [categoryRoots]);

  // Handlers
  const handleMetricChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const metric = e.target.value;
    setSelectedMetric(metric);
    onConfigChange?.({ metric });
  }, [onConfigChange]);

  const handleRangeChange = useCallback((newRange: RangeKey) => {
    setRange(newRange);
    onConfigChange?.({ time_range: newRange });
  }, [onConfigChange]);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    setCategory(next);
    onConfigChange?.({ category: next });
  }, [onConfigChange]);

  // Sync state with config when config changes externally
  useEffect(() => {
    if (config.metric) {
      const validMetric = getValidMetricKey(config.metric, MetricKeys.PriceClose);
      setSelectedMetric(validMetric);
    }
    if (config.time_range) {
      setRange(config.time_range as RangeKey);
    }
    if (config.category) {
      setCategory(config.category);
    }
  }, [config.metric, config.time_range, config.category]);

  const metricLabel = useMemo(() => {
    return METRIC_OPTIONS.find((m) => m.key === selectedMetric)?.label ?? selectedMetric;
  }, [selectedMetric]);

  const categoryLabel = useMemo(() => {
    if (category === "all") return "All Tokens";
    const match = categoryOptions.find((cat) => cat.category_code === category);
    return match?.category_name ?? category;
  }, [category, categoryOptions]);

  const chartTitle = useMemo(() => {
    return `Correlation Matrix - ${metricLabel}, ${categoryLabel}`;
  }, [metricLabel, categoryLabel]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          {chartTitle}
        </span>
      </div>
      {/* Controls */}
      <div className="flex-shrink-0 flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-700/30">
        {/* Metric selector */}
        <select
          value={selectedMetric}
          onChange={handleMetricChange}
          className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0"
        >
          {METRIC_OPTIONS.map(m => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>

        {/* Category selector */}
        <select
          value={category}
          onChange={handleCategoryChange}
          className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0 max-w-[100px]"
        >
          <option value="all">All</option>
          {categoryOptions.map((cat) => (
            <option key={cat.category_code} value={cat.category_code}>
              {cat.category_name}
            </option>
          ))}
        </select>

        {/* Time range */}
        <div className="ml-auto">
          <TimeRangePicker
            options={['1d', '7d', '30d', '365d'] as const}
            value={range}
            onChange={(v) => handleRangeChange(v as RangeKey)}
            theme="blue"
          />
        </div>
      </div>

      {/* Chart container */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <CorrelationChart
          rows={rowsFiltered as Array<Record<string, unknown>>}
          range={range}
          xKey={selectedMetric}
          search=""
          mcMin=""
          mcMax=""
          labelFromCatalog={labelFromCatalog}
        />
      </div>
    </div>
  );
};

export default CorrelationMatrixWidget;
